/* verqo — /dropship · dedicated landing page
 *
 * Strategy:
 *   • Audience-aware (?as=seller / ?as=buyer) — same page, slight tone shift
 *   • Lead with the "Dropship Loop" animated diagram (5 players, one motion)
 *   • Big differentiator: BNPL + Insurance + Day-1 supplier payouts baked in
 *   • Comparison fight card vs Modern Dropship / Canal / Manual
 *   • Style mirrors / (seller) + /buyers (buyer) landings exactly
 *
 * Section order:
 *   nav → hero → trust strip → audience split → the loop diagram →
 *   the math → live sync feed → built-in moats → comparison →
 *   pricing → roadmap → faq → cta → footer
 */
import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, CheckCircle2, CircleDot, ChevronDown,
  Banknote, ShieldCheck, Truck, Zap, Network, Boxes,
  Building2, Store, ShoppingBasket, Plug,
  Sparkles, Package, X as XIcon, Check, AlertTriangle,
  Globe, Layers, Receipt, Camera, LayoutDashboard,
} from "lucide-react";
import { VerqoLockup } from "../../components/VerqoLockup";
import MarketingMobileNav from "../../components/MarketingMobileNav";

// ── Shared atoms ───────────────────────────────────────────────────────────
const Eyebrow = ({ children, testid }) => (
  <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.28em] text-lime-700 font-semibold" data-testid={testid}>
    <CircleDot className="w-2.5 h-2.5 fill-lime-500 text-lime-500"/>
    {children}
  </div>
);

const Reveal = ({ children, delay = 0, y = 24 }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

const PrimaryCTA = ({ href = "/waitlist?as=seller", children }) => (
  <a href={href} className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition-all bg-[#0c1612]">
    {children}
    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform"/>
  </a>
);

const SecondaryCTA = ({ href = "#how", children }) => (
  <a href={href} className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg border border-slate-300 text-slate-900 text-sm hover:bg-slate-50 transition-colors">
    {children}
  </a>
);

// ─────────────────────────────────────────────────────────────────────────
// VISUAL 1 — The Dropship Loop (animated 5-node circular diagram)
// Adapts the NetworkGraph aesthetic with kinetic flow lines
// ─────────────────────────────────────────────────────────────────────────
function DropshipLoop() {
  const nodes = [
    { id: "supplier", label: "Supplier", icon: Building2,   angle: -90  },
    { id: "verqo",    label: "verqo",    icon: Layers,      angle: -18, isHub: true },
    { id: "retailer", label: "Retailer", icon: Store,       angle:  54  },
    { id: "customer", label: "Customer", icon: ShoppingBasket, angle: 126 },
    { id: "carrier",  label: "Carrier",  icon: Truck,       angle: 198 },
  ];
  const cx = 200, cy = 200, r = 130;
  const polar = (a) => ({ x: cx + r * Math.cos(a * Math.PI / 180), y: cy + r * Math.sin(a * Math.PI / 180) });
  const positions = nodes.map(n => ({ ...n, ...polar(n.angle) }));
  // Arrange order of flow lines (which connect to which next)
  const flowEdges = [
    [0, 1, "catalog synced"],     // supplier → verqo
    [1, 2, "1-click import"],     // verqo → retailer
    [2, 3, "order placed"],       // retailer → customer
    [3, 1, "verqo routes"],       // customer (order) → verqo
    [1, 0, "paid Day 1"],         // verqo → supplier
    [0, 4, "ship + track"],       // supplier → carrier
    [4, 3, "delivered"],          // carrier → customer
  ];

  return (
    <div className="relative rounded-2xl p-6 md:p-7 overflow-hidden shadow-[0_40px_80px_-32px_rgba(15,23,42,0.45)] bg-[#0c1612]" data-testid="dropship-loop">
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
           style={{ backgroundImage: "linear-gradient(rgba(220,255,58,1) 1px, transparent 1px), linear-gradient(90deg, rgba(220,255,58,1) 1px, transparent 1px)", backgroundSize: "28px 28px" }}/>
      <div className="absolute inset-0 opacity-30 pointer-events-none"
           style={{ background: "radial-gradient(circle at 50% 50%, rgba(220,255,58,0.18) 0%, transparent 55%)" }}/>
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.26em] font-semibold text-lime-400">The dropship loop</div>
          <div className="inline-flex items-center gap-1.5 text-[10px] text-white/55 uppercase tracking-wider">
            <span className="w-1.5 h-1.5 rounded-full bg-lime-300 animate-pulse"/> Live
          </div>
        </div>
        <svg viewBox="0 0 400 400" className="w-full h-auto max-h-[420px]">
          {/* Outer ring */}
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(220,255,58,0.10)" strokeWidth="1" strokeDasharray="2 6"/>
          {/* Flow lines with animated dots */}
          {flowEdges.map(([a, b, label], i) => {
            const p1 = positions[a], p2 = positions[b];
            return (
              <g key={i}>
                <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
                      stroke="rgba(220,255,58,0.16)" strokeWidth="1"/>
                <circle r="3" fill="#dcff3a">
                  <animateMotion
                    dur={`${4 + i * 0.3}s`} repeatCount="indefinite"
                    keyTimes="0;1" keySplines="0.4 0 0.2 1" calcMode="spline"
                    begin={`${i * 0.6}s`}
                    path={`M${p1.x} ${p1.y} L${p2.x} ${p2.y}`}/>
                  <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.1;0.9;1"
                           dur={`${4 + i * 0.3}s`} repeatCount="indefinite" begin={`${i * 0.6}s`}/>
                </circle>
              </g>
            );
          })}
          {/* Nodes */}
          {positions.map((n) => (
            <g key={n.id}>
              {n.isHub && (
                <>
                  <circle cx={n.x} cy={n.y} r="38" fill="#dcff3a"/>
                  <foreignObject x={n.x - 18} y={n.y - 18} width="36" height="36">
                    <div className="w-full h-full grid place-items-center">
                      <Layers className="w-6 h-6 text-slate-900" strokeWidth={2}/>
                    </div>
                  </foreignObject>
                </>
              )}
              {!n.isHub && (
                <>
                  <circle cx={n.x} cy={n.y} r="28" fill="#ffffff"
                          style={{ filter: "drop-shadow(0 0 12px rgba(220,255,58,0.5))" }}/>
                  <foreignObject x={n.x - 14} y={n.y - 14} width="28" height="28">
                    <div className="w-full h-full grid place-items-center">
                      <n.icon className="w-4 h-4 text-slate-900" strokeWidth={1.6}/>
                    </div>
                  </foreignObject>
                </>
              )}
              <text x={n.x} y={n.y + (n.isHub ? 52 : 44)} textAnchor="middle"
                    className="fill-white/80 text-[10px] font-semibold uppercase tracking-[0.2em]">
                {n.label}
              </text>
            </g>
          ))}
        </svg>
        <div className="mt-2 text-center">
          <div className="text-[18px] md:text-[20px] font-semibold text-white tracking-tight leading-tight">
            One loop. <span className="text-white/45">Five players. Zero friction.</span>
          </div>
          <div className="text-[11.5px] text-white/55 mt-1.5 max-w-[340px] mx-auto leading-relaxed">
            verqo routes every order, payment, and shipment in real time — no manual handoffs, no spreadsheets, no integrations to maintain.
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// VISUAL 2 — "The Math" animated count-up
// ─────────────────────────────────────────────────────────────────────────
function MathRow({ label, traditional, verqo, highlight = false, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className="grid grid-cols-[1.2fr_1fr_1fr] gap-3 py-3 border-b border-slate-200/70 last:border-0">
      <div className="text-[12.5px] text-slate-600">{label}</div>
      <div className="text-[14px] text-slate-500 line-through opacity-70">{traditional}</div>
      <div className={`text-[14px] font-semibold ${highlight ? "text-lime-700" : "text-slate-900"}`}>{verqo}</div>
    </motion.div>
  );
}
function TheMath() {
  const rows = [
    ["Supplier gets paid",   "Day 60–90",          "Day 1",            true],
    ["Retailer pays upfront","100% cash",          "$0 (Net-60)",      true],
    ["Bad-debt risk",        "100% on you",        "$0 (Allianz)",     true],
    ["Platform fee (buyers)","3–5% of sales",      "$0",               true],
    ["Tools needed",         "5+ apps",            "1 portal",         true],
    ["Setup time",           "2–6 weeks",          "1 day",            true],
  ];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8" data-testid="dropship-math">
      <div className="grid grid-cols-[1.2fr_1fr_1fr] gap-3 pb-3 border-b border-slate-300">
        <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-slate-500"></div>
        <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-slate-500">Traditional dropship</div>
        <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-lime-700 font-semibold">verqo</div>
      </div>
      {rows.map((r, i) => (
        <MathRow key={r[0]} label={r[0]} traditional={r[1]} verqo={r[2]} highlight={r[3]} delay={i * 0.08}/>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// VISUAL 3 — Live sync feed (Stripe-style scrolling event log)
// ─────────────────────────────────────────────────────────────────────────
function LiveSyncFeed() {
  const SAMPLE_EVENTS = [
    { t: "✓", c: "lime",  msg: "Apex Goods imported \"Cashmere Throw\" — listed at $89.99" },
    { t: "→", c: "sky",   msg: "Order #4023 routed to GlowCo — paid in 0.3s" },
    { t: "✓", c: "lime",  msg: "FedEx tracking 7392-4181-XX received from EZbase" },
    { t: "$", c: "amber", msg: "Buyer cover: $250K approved for Bella Foods" },
    { t: "✓", c: "lime",  msg: "Invoice #INV-2018 paid Day 1 via Net-60 line" },
    { t: "→", c: "sky",   msg: "Stripe payout settled — $48,200 to Beautify Co" },
    { t: "✓", c: "lime",  msg: "Verqo Shipping label printed for order #4019 — UPS Ground" },
    { t: "$", c: "amber", msg: "AI credit decision: Saks Avenue approved Net-90 in 41s" },
    { t: "→", c: "sky",   msg: "Inventory sync: 1,204 SKUs updated across 8 retailers" },
    { t: "✓", c: "lime",  msg: "Allianz claim auto-filed on overdue invoice #INV-1881" },
    { t: "→", c: "sky",   msg: "Sync rule \"Beauty under $50\" added 12 new SKUs" },
    { t: "✓", c: "lime",  msg: "Return verified with photo evidence — $89.99 credited" },
  ];
  const [feed, setFeed] = useState(SAMPLE_EVENTS.slice(0, 6));
  const cursorRef = useRef(6);
  useEffect(() => {
    const id = setInterval(() => {
      cursorRef.current = (cursorRef.current + 1) % SAMPLE_EVENTS.length;
      setFeed(prev => [SAMPLE_EVENTS[cursorRef.current], ...prev.slice(0, 5)]);
    }, 2400);
    return () => clearInterval(id);
  }, []);

  const dotColor = { lime: "bg-lime-400", sky: "bg-sky-400", amber: "bg-amber-400" };
  const now = new Date();
  const timeStr = (offset) => {
    const d = new Date(now.getTime() - offset * 1000);
    return d.toTimeString().slice(0, 8);
  };

  return (
    <div className="rounded-2xl bg-[#0c1612] p-6 md:p-7 overflow-hidden border border-white/5 shadow-[0_40px_80px_-32px_rgba(15,23,42,0.45)]" data-testid="dropship-live-feed">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] uppercase tracking-[0.22em] text-lime-300 font-semibold">Live network · 30-day sample</div>
        <div className="inline-flex items-center gap-1.5 text-[10px] text-white/55 uppercase tracking-wider">
          <span className="w-1.5 h-1.5 rounded-full bg-lime-300 animate-pulse"/> Streaming
        </div>
      </div>
      <div className="space-y-1.5 font-mono text-[12px] min-h-[180px]">
        {feed.map((e, i) => (
          <motion.div
            key={`${e.msg}-${i}`}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: i === 0 ? 1 : Math.max(0.3, 1 - i * 0.15), x: 0 }}
            transition={{ duration: 0.4 }}
            className="flex gap-3 items-center text-white/80">
            <span className="text-white/40 tabular-nums">{timeStr(i * 3)}</span>
            <span className={`w-1.5 h-1.5 rounded-full ${dotColor[e.c]}`}/>
            <span className="truncate">{e.msg}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// VISUAL 4 — Comparison fight card
// ─────────────────────────────────────────────────────────────────────────
function ComparisonTable() {
  const COMPETITORS = [
    { name: "verqo",           highlight: true,  bg: "bg-lime-50",  border: "border-lime-400 ring-2 ring-lime-400/40" },
    { name: "Modern Dropship", highlight: false, bg: "bg-white",    border: "border-slate-200" },
    { name: "Canal",           highlight: false, bg: "bg-white",    border: "border-slate-200" },
    { name: "Manual / DIY",    highlight: false, bg: "bg-white",    border: "border-slate-200" },
  ];
  const ROWS = [
    ["Setup time",                   "1 day",      "2 weeks",     "3 days",     "6 weeks"],
    ["Supplier paid",                "Day 1",      "Day 30-90",   "Day 30-90",  "Day 60-90"],
    ["Bad-debt risk",                "$0 (insured)", "100% on you", "100%",      "100%"],
    ["Buyer-side platform fee",      "$0",         "3-5%",        "5%",         "—"],
    ["Credit decisions",             "60 seconds", "Not offered", "Not offered","2-6 weeks"],
    ["Invoice coverage (Allianz)",   true,         false,         false,        false],
    ["Net-30/60/90 financing baked in", true,      false,         false,        false],
    ["Same portal as wholesale ops", true,         false,         false,        false],
    ["Shopify / Woo / BigCommerce sync", true,     true,          true,         false],
    ["EDI / CSV support",            "CSV today, EDI roadmap", true, "Partial", false],
    ["Saved, re-runnable sync rules", true,        false,         false,        false],
    ["Photo-evidenced returns flow",  true,        false,         false,        false],
    ["Stalled order detection (SLA)", true,        false,         false,        false],
    ["Multi-store per supplier",      true,        "Limited",     false,        false],
    ["Daily consolidated invoices",   true,        false,         false,        false],
  ];
  const Cell = ({ v, highlight }) => {
    if (v === true)  return <Check className={`w-4 h-4 mx-auto ${highlight ? "text-lime-700" : "text-emerald-600"}`} strokeWidth={3}/>;
    if (v === false) return <XIcon className="w-4 h-4 mx-auto text-rose-500"/>;
    return <span className={`text-[12.5px] ${highlight ? "text-lime-900 font-semibold" : "text-slate-700"}`}>{v}</span>;
  };
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="dropship-comparison">
      <table className="w-full text-[13px] min-w-[760px]">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="text-left p-4 text-[10.5px] mono uppercase tracking-[0.2em] text-slate-500 font-medium">vs</th>
            {COMPETITORS.map((c, i) => (
              <th key={c.name} className={`p-4 text-center ${c.bg} ${i === 0 ? "border-l-2 border-r-2 " + c.border : ""}`}>
                <div className={`text-[14px] tracking-tight ${c.highlight ? "text-lime-900 font-bold" : "text-slate-900 font-semibold"}`}>
                  {c.name}
                  {c.highlight && <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] bg-slate-900 text-lime-300 uppercase tracking-wider">You are here</span>}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ROWS.map(([label, ...cells], i) => (
            <tr key={label} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors">
              <td className="p-4 text-slate-700 font-medium">{label}</td>
              {cells.map((v, j) => (
                <td key={j} className={`p-4 text-center ${j === 0 ? COMPETITORS[0].bg + " border-l-2 border-r-2 " + COMPETITORS[0].border : ""}`}>
                  <Cell v={v} highlight={j === 0}/>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// FAQs
// ─────────────────────────────────────────────────────────────────────────
const DROPSHIP_FAQS = [
  {
    q: "How is verqo different from Modern Dropship?",
    a: "Modern Dropship is a great catalog sync platform — but you still need a separate BNPL, insurance, and credit underwriting solution. verqo bakes all of that into the same portal: Day-1 payouts to suppliers, Allianz-backed cover on every invoice, AI credit decisions in 60 seconds, and one ledger across the whole network. You replace 5 tools with 1.",
  },
  {
    q: "How fast is supplier payout actually?",
    a: "Day 1. The moment a retailer's order is paid (or financed via Net-30/60/90), we settle the supplier's wallet within 24 hours via TabaPay ACH. The retailer keeps their full payment terms — verqo carries the float and underwrites the credit risk.",
  },
  {
    q: "Do I need Shopify to use this?",
    a: "No. We integrate with Shopify, WooCommerce, BigCommerce, and accept CSV uploads. EDI support is on the 2026 roadmap. If you're a brand with a Shopify store, the catalog sync is one click. If you're EDI-only today, we'll get you live within a week.",
  },
  {
    q: "What does it cost?",
    a: "Free for retailers — $0 platform fee, ever. Suppliers pay a small percentage (typically 1-3%, depending on volume and term length). Pass-through costs apply normally (carrier shipping, card processing on the buyer side, etc.) and we don't mark those up.",
  },
  {
    q: "What categories does dropship work in?",
    a: "All of them. Today we're strongest in fashion, beauty, food & beverage, home goods, pet, and wellness — but the platform is category-agnostic. If you can ship it, you can dropship it through verqo.",
  },
  {
    q: "Can my existing suppliers come onto verqo?",
    a: "Yes. Use the PO Generator (free for retailers) to issue a verqo-issued PO to any off-network supplier. They onboard in minutes to fulfill it. From the second order onward, you're on the verqo network with all financing and insurance benefits.",
  },
];

// ─────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────
export default function DropshipLanding() {
  const [params] = useSearchParams();
  const audience = params.get("as") === "buyer" ? "buyer" : "seller";

  useEffect(() => {
    document.title = "verqo for dropship · The wholesale OS that includes dropship";
  }, []);

  // Rotating hero word
  const HERO_WORDS = [
    "supplier paid Day 1.",
    "buyer covered, no risk.",
    "live in 1 click.",
    "zero apps, zero spreadsheets.",
  ];
  const [heroIdx, setHeroIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setHeroIdx((x) => (x + 1) % HERO_WORDS.length), 2500);
    return () => clearInterval(t);
  }, []);

  // FAQ accordion
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div
      className="min-h-screen text-slate-900 antialiased selection:bg-lime-300 selection:text-slate-900"
      style={{ background: "#FAFAF7" }}
      data-testid="dropship-landing"
    >
      <style>{`
        @keyframes verqo-word-rotate {
          0%   { opacity: 0.05; transform: translateY(8px); }
          100% { opacity: 1;    transform: translateY(0); }
        }
        .verqo-word-rotate { animation: verqo-word-rotate 0.22s ease-out; }
      `}</style>

      {/* ─────────────────── NAV ─────────────────── */}
      <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-md bg-white/80 border-b border-slate-200/60">
        <div className="max-w-[1280px] mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center group" data-testid="dropship-nav-home">
            <VerqoLockup/>
            <span className="ml-1 text-[11px] text-slate-500 hidden lg:inline group-hover:text-slate-900 transition-colors">· Wholesale, instant.</span>
          </Link>
          <nav className="hidden lg:flex items-center gap-7 text-[12px] text-slate-600">
            <a href="#how"        className="hover:text-slate-900 transition-colors">How it works</a>
            <a href="#math"       className="hover:text-slate-900 transition-colors">The math</a>
            <a href="#vs"         className="hover:text-slate-900 transition-colors">vs. competitors</a>
            <a href="#pricing"    className="hover:text-slate-900 transition-colors">Pricing</a>
            <Link to="/"          className="text-slate-500 hover:text-slate-900 transition-colors">For sellers</Link>
            <Link to="/buyers"    className="text-slate-500 hover:text-slate-900 transition-colors">For buyers</Link>
          </nav>
          <div className="flex items-center gap-3">
            <a href="/login" className="text-[12.5px] text-slate-600 hover:text-slate-900 hidden md:inline">Sign in</a>
            <PrimaryCTA href={audience === "buyer" ? "/waitlist?as=buyer" : "/waitlist?as=seller"}>
              {audience === "buyer" ? "Get pre-approved" : "Get started"}
            </PrimaryCTA>
            <MarketingMobileNav links={[
              { label: "How it works", href: "#how" },
              { label: "The math",     href: "#math" },
              { label: "Compare",      href: "#vs" },
              { label: "Pricing",      href: "#pricing" },
              { label: "For sellers",  href: "/" },
              { label: "For buyers",   href: "/buyers" },
            ]} cta={{ label: "Get started", href: "/waitlist?as=seller" }}/>
          </div>
        </div>
      </header>

      {/* ─────────────────── HERO ─────────────────── */}
      <section className="relative pt-28 md:pt-36 pb-12 md:pb-16 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="dropship-hero">
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16 items-center">
          <div>
            <Reveal>
              <Eyebrow testid="dropship-eyebrow">Dropship · the wholesale-OS edition</Eyebrow>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="mt-5 text-[40px] md:text-[58px] lg:text-[72px] leading-[0.98] tracking-tight font-semibold text-slate-900" data-testid="dropship-headline">
                Dropship,<br/>
                <span key={heroIdx} className="bg-lime-300 px-2 -rotate-[0.4deg] inline-block my-1 verqo-word-rotate" data-testid="dropship-rotating-word">
                  {HERO_WORDS[heroIdx]}
                </span>
              </h1>
            </Reveal>
            <Reveal delay={0.22}>
              <p className="mt-7 max-w-xl text-[16px] md:text-[18px] leading-relaxed text-slate-600" data-testid="dropship-subhead">
                The only dropship platform built inside a complete wholesale OS — <strong className="text-slate-900">suppliers paid instantly, retailers insured on every invoice, and your entire wholesale ledger in one portal.</strong>
              </p>
            </Reveal>
            <Reveal delay={0.32}>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <PrimaryCTA href={audience === "buyer" ? "/waitlist?as=buyer" : "/waitlist?as=seller"}>
                  {audience === "buyer" ? "Start dropshipping (free)" : "Onboard your catalog"}
                </PrimaryCTA>
                <SecondaryCTA href="#how">See the loop</SecondaryCTA>
              </div>
            </Reveal>
            <Reveal delay={0.44}>
              <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12px] text-slate-500">
                <div className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-lime-600"/> Day-1 supplier payouts</div>
                <div className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-lime-600"/> Insured on every invoice</div>
                <div className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-lime-600"/> Free for retailers</div>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15} y={32}>
            <DropshipLoop/>
          </Reveal>
        </div>
      </section>

      {/* ─────────────────── TRUST STRIP ─────────────────── */}
      <section className="relative py-10 border-y border-slate-200/70 bg-white/60">
        <div className="max-w-[1280px] mx-auto px-6 md:px-12">
          <div className="text-center text-[10.5px] mono uppercase tracking-[0.28em] text-slate-500 mb-5">
            Plugs into the stack you already run
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-slate-700 text-[15px] font-semibold tracking-tight">
            <span className="inline-flex items-center gap-2"><Plug className="w-4 h-4 text-lime-700"/>Shopify</span>
            <span className="inline-flex items-center gap-2"><Plug className="w-4 h-4 text-lime-700"/>WooCommerce</span>
            <span className="inline-flex items-center gap-2"><Plug className="w-4 h-4 text-lime-700"/>BigCommerce</span>
            <span className="inline-flex items-center gap-2"><Globe className="w-4 h-4 text-lime-700"/>NetSuite</span>
            <span className="inline-flex items-center gap-2"><Truck className="w-4 h-4 text-lime-700"/>Verqo Shipping</span>
            <span className="inline-flex items-center gap-2"><Boxes className="w-4 h-4 text-lime-700"/>CSV import</span>
          </div>
        </div>
      </section>

      {/* ─────────────────── AUDIENCE SPLIT ─────────────────── */}
      <section id="paths" className="relative py-20 md:py-24 px-6 md:px-12 max-w-[1280px] mx-auto">
        <Reveal>
          <div className="text-center mb-12">
            <Eyebrow testid="dropship-paths-eyebrow">Built for both sides of the trade</Eyebrow>
            <h2 className="mt-4 display text-3xl md:text-5xl font-semibold tracking-tight text-slate-900">
              Whether you ship it or sell it.
            </h2>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Reveal delay={0.05}>
            <div className="rounded-2xl border border-slate-200 bg-white p-7 md:p-9 h-full" data-testid="dropship-for-suppliers">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-9 h-9 rounded-lg bg-slate-900 text-lime-300 grid place-items-center">
                  <Building2 className="w-5 h-5" strokeWidth={1.8}/>
                </div>
                <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-slate-500">For suppliers / brands</div>
              </div>
              <h3 className="text-[22px] font-semibold tracking-tight text-slate-900">Sell to 500+ retailers without lifting a finger.</h3>
              <ul className="mt-5 space-y-2.5 text-[13.5px] text-slate-700">
                <li className="flex gap-2 items-start"><CheckCircle2 className="w-4 h-4 text-lime-600 mt-0.5 shrink-0"/>One-click Shopify catalog sync — your products go live across the network</li>
                <li className="flex gap-2 items-start"><CheckCircle2 className="w-4 h-4 text-lime-600 mt-0.5 shrink-0"/>Day-1 payouts — verqo finances the buyer; you get paid tomorrow</li>
                <li className="flex gap-2 items-start"><CheckCircle2 className="w-4 h-4 text-lime-600 mt-0.5 shrink-0"/>Set your own margins, MOQs, and shipping rules per retailer</li>
                <li className="flex gap-2 items-start"><CheckCircle2 className="w-4 h-4 text-lime-600 mt-0.5 shrink-0"/>Allianz cover means zero risk on every order</li>
              </ul>
              <div className="mt-7">
                <a href="/waitlist?as=seller" className="inline-flex items-center gap-2 text-[13.5px] font-semibold text-slate-900 group">
                  Onboard your catalog
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform"/>
                </a>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="rounded-2xl border-2 border-lime-400 bg-lime-50 p-7 md:p-9 h-full" data-testid="dropship-for-retailers">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-9 h-9 rounded-lg bg-lime-300 text-slate-900 grid place-items-center">
                  <Store className="w-5 h-5" strokeWidth={1.8}/>
                </div>
                <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-lime-800">For retailers / buyers</div>
              </div>
              <h3 className="text-[22px] font-semibold tracking-tight text-slate-900">Stock anything. Pay zero upfront.</h3>
              <ul className="mt-5 space-y-2.5 text-[13.5px] text-slate-800">
                <li className="flex gap-2 items-start"><CheckCircle2 className="w-4 h-4 text-lime-700 mt-0.5 shrink-0"/>Import any product to your Shopify in 1 click — no inventory risk</li>
                <li className="flex gap-2 items-start"><CheckCircle2 className="w-4 h-4 text-lime-700 mt-0.5 shrink-0"/>Pay on Net-30/60/90 — never tie up cash for inventory</li>
                <li className="flex gap-2 items-start"><CheckCircle2 className="w-4 h-4 text-lime-700 mt-0.5 shrink-0"/>$0 platform fees — ever. We work for the supplier.</li>
                <li className="flex gap-2 items-start"><CheckCircle2 className="w-4 h-4 text-lime-700 mt-0.5 shrink-0"/>Real-time order, shipping, and inventory sync across every supplier</li>
              </ul>
              <div className="mt-7">
                <a href="/waitlist?as=buyer" className="inline-flex items-center gap-2 text-[13.5px] font-semibold text-slate-900 group">
                  Get pre-approved — free
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform"/>
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─────────────────── HOW IT WORKS · 5 STEPS ─────────────────── */}
      <section id="how" className="relative py-20 md:py-24 px-6 md:px-12 max-w-[1280px] mx-auto">
        <Reveal>
          <div className="text-center mb-12">
            <Eyebrow>How the loop works</Eyebrow>
            <h2 className="mt-4 display text-3xl md:text-5xl font-semibold tracking-tight text-slate-900">
              From catalog to delivered. <span className="text-slate-400">5 steps. 1 portal.</span>
            </h2>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-5">
          {[
            { n: 1, icon: Boxes,        title: "Supplier syncs catalog",   body: "Connect Shopify, Woo, BigCommerce, or paste a CSV. Add re-runnable sync rules to auto-include products by category, price, or stock — no manual work.", },
            { n: 2, icon: Sparkles,     title: "Retailer 1-click imports", body: "Browse the catalog, pick products, set markup. They appear in your Shopify storefront instantly.", },
            { n: 3, icon: ShoppingBasket, title: "End customer orders",    body: "Customer buys from the retailer's store. Order auto-routes to the supplier in real time.", },
            { n: 4, icon: Banknote,     title: "Supplier paid Day 1",      body: "verqo settles the supplier's wallet within 24h. Retailer gets Net-30/60/90 on the invoice.", },
            { n: 5, icon: Truck,        title: "Shipped & tracked",        body: "Supplier ships with Verqo Shipping. Tracking auto-flows to retailer and end customer.", },
          ].map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className="rounded-2xl border border-slate-200 bg-white p-5 h-full" data-testid={`how-step-${s.n}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-9 h-9 rounded-lg bg-slate-900 text-lime-300 grid place-items-center">
                    <s.icon className="w-4 h-4" strokeWidth={1.8}/>
                  </div>
                  <span className="text-[11px] mono text-slate-400">0{s.n}</span>
                </div>
                <div className="text-[15px] font-semibold text-slate-900 leading-tight">{s.title}</div>
                <div className="text-[12.5px] text-slate-600 mt-2 leading-relaxed">{s.body}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ─────────────────── POWER FEATURES BENTO ─────────────────── */}
      <section className="relative py-20 md:py-24 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="dropship-power-features">
        <Reveal>
          <div className="text-center mb-12">
            <Eyebrow>Power features competitors don't bundle</Eyebrow>
            <h2 className="mt-4 display text-3xl md:text-5xl font-semibold tracking-tight text-slate-900">
              The deep stuff. <span className="text-slate-400">Built in.</span>
            </h2>
            <p className="mt-4 text-[14.5px] text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Sync rules, daily invoices, photo-evidenced returns, multi-store support, and SLA monitoring — all in the same portal, no extra plugins.
            </p>
          </div>
        </Reveal>

        {/* Bento — large hero tile + 4 small tiles + 1 full-width Enterprise banner */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* HERO TILE — Sync Rules (2 cols × 2 rows) */}
          <Reveal delay={0.05}>
            <div className="md:col-span-2 md:row-span-2 rounded-3xl bg-[#0c1612] text-white p-8 md:p-10 relative overflow-hidden h-full" data-testid="power-sync-rules">
              <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
                   style={{ backgroundImage: "linear-gradient(rgba(220,255,58,1) 1px, transparent 1px), linear-gradient(90deg, rgba(220,255,58,1) 1px, transparent 1px)", backgroundSize: "28px 28px" }}/>
              <div className="relative">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-lime-300 text-slate-900 mb-4">
                  <Sparkles className="w-6 h-6" strokeWidth={1.6}/>
                </div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-lime-300 font-semibold mb-1">Saved sync rules engine</div>
                <h3 className="text-[24px] md:text-[30px] font-semibold tracking-tight leading-[1.05]">
                  Build it once.<br/>It runs forever.
                </h3>
                <p className="mt-3 text-[13.5px] text-white/65 leading-relaxed max-w-md">
                  Save re-runnable rules like <em>"auto-include all beauty SKUs under $50, in stock, not drafts."</em> Preview before applying. One-click re-run. We track how many products each rule moved last time.
                </p>
                {/* Mini visual — fake rule preview */}
                <div className="mt-6 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-4 max-w-md">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[9.5px] mono uppercase tracking-[0.2em] text-lime-300/80">RULE · BEAUTY {"<"}$50</span>
                    <span className="ml-auto text-[10px] text-white/45">last ran · added 12</span>
                  </div>
                  <div className="space-y-1.5 text-[11.5px] text-white/70 font-mono">
                    <div className="flex gap-2 items-center"><Check className="w-3 h-3 text-lime-400 shrink-0"/>category = "beauty"</div>
                    <div className="flex gap-2 items-center"><Check className="w-3 h-3 text-lime-400 shrink-0"/>price &lt;= $50</div>
                    <div className="flex gap-2 items-center"><Check className="w-3 h-3 text-lime-400 shrink-0"/>stock {">"} 0  ·  not draft</div>
                  </div>
                </div>
                <div className="mt-5 text-[11.5px] text-lime-300/70 italic">
                  Other tools give you filters. We give you an engine.
                </div>
              </div>
            </div>
          </Reveal>

          {/* TILE 2 — Daily Invoices */}
          <Reveal delay={0.10}>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 h-full flex flex-col hover:border-slate-900 hover:shadow-[0_24px_60px_-24px_rgba(15,23,42,0.25)] transition-all" data-testid="power-daily-invoices">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-slate-900 text-lime-300 mb-3">
                <Receipt className="w-5 h-5" strokeWidth={1.8}/>
              </div>
              <div className="text-[10.5px] mono uppercase tracking-wider text-slate-400 mb-1">Daily consolidated invoices</div>
              <div className="text-[15px] font-semibold text-slate-900 leading-tight">One invoice per buyer, per day.</div>
              <div className="text-[12.5px] text-slate-600 mt-2 leading-relaxed">
                Not one invoice per order — that's email-bombing the AP team. Yours batches daily.
              </div>
              {/* Mini visual — stacked invoice card */}
              <div className="mt-auto pt-5">
                <div className="relative h-16">
                  <div className="absolute inset-x-2 top-2 h-10 rounded-md bg-slate-100 border border-slate-200"/>
                  <div className="absolute inset-x-1 top-1 h-10 rounded-md bg-slate-50 border border-slate-200"/>
                  <div className="absolute inset-x-0 top-0 h-10 rounded-md bg-white border border-slate-300 shadow-sm flex items-center px-3 gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-lime-500"/>
                    <span className="text-[10.5px] font-semibold text-slate-700">INV-2026-04-12</span>
                    <span className="text-[10px] text-slate-500 ml-auto">28 orders · $14,820</span>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* TILE 3 — Photo Returns */}
          <Reveal delay={0.15}>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 h-full flex flex-col hover:border-slate-900 hover:shadow-[0_24px_60px_-24px_rgba(15,23,42,0.25)] transition-all" data-testid="power-returns">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-slate-900 text-lime-300 mb-3">
                <Camera className="w-5 h-5" strokeWidth={1.8}/>
              </div>
              <div className="text-[10.5px] mono uppercase tracking-wider text-slate-400 mb-1">Photo-evidenced returns</div>
              <div className="text-[15px] font-semibold text-slate-900 leading-tight">Bilateral. Auditable. Zero disputes.</div>
              <div className="text-[12.5px] text-slate-600 mt-2 leading-relaxed">
                Buyer files with photos → seller verifies or rejects with counter-photos → refund auto-credits the next daily invoice.
              </div>
              {/* Mini visual — 3 photo thumbnails */}
              <div className="mt-auto pt-5 flex gap-1.5">
                {[
                  { tint: "bg-rose-100",   icon: "📦", note: "Damaged" },
                  { tint: "bg-amber-100",  icon: "📷", note: "Evidence" },
                  { tint: "bg-emerald-100",icon: "✓",  note: "Verified" },
                ].map((p, i) => (
                  <div key={i} className={`flex-1 aspect-square rounded-md ${p.tint} border border-slate-200 flex flex-col items-center justify-center text-[10px] text-slate-700`}>
                    <span className="text-base leading-none">{p.icon}</span>
                    <span className="mt-0.5 mono">{p.note}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* TILE 4 — Stalled Orders */}
          <Reveal delay={0.20}>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 h-full hover:border-slate-900 hover:shadow-[0_24px_60px_-24px_rgba(15,23,42,0.25)] transition-all" data-testid="power-stalled">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-amber-100 text-amber-700 mb-3">
                <AlertTriangle className="w-5 h-5" strokeWidth={1.8}/>
              </div>
              <div className="text-[10.5px] mono uppercase tracking-wider text-slate-400 mb-1">Stalled order detection</div>
              <div className="text-[15px] font-semibold text-slate-900 leading-tight">Catch problems before they breach SLA.</div>
              <div className="text-[12.5px] text-slate-600 mt-2 leading-relaxed">
                "Hours since movement" badges surface silently-stuck orders so you fix them proactively.
              </div>
            </div>
          </Reveal>

          {/* TILE 5 — Multi-store */}
          <Reveal delay={0.25}>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 h-full hover:border-slate-900 hover:shadow-[0_24px_60px_-24px_rgba(15,23,42,0.25)] transition-all" data-testid="power-multi-store">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-slate-900 text-lime-300 mb-3">
                <Store className="w-5 h-5" strokeWidth={1.8}/>
              </div>
              <div className="text-[10.5px] mono uppercase tracking-wider text-slate-400 mb-1">Multi-store support</div>
              <div className="text-[15px] font-semibold text-slate-900 leading-tight">1, 5, or 50 stores. Same account.</div>
              <div className="text-[12.5px] text-slate-600 mt-2 leading-relaxed">
                Connect every Shopify / Woo / BigCommerce storefront you own. Each gets its own sync rules + analytics.
              </div>
            </div>
          </Reveal>

          {/* TILE 6 — Admin Control Center (full-width Enterprise banner) */}
          <div className="md:col-span-3">
          <Reveal delay={0.30}>
            <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6 md:p-8 relative overflow-hidden flex flex-col md:flex-row md:items-center gap-5" data-testid="power-admin">
              <div className="absolute top-3 right-3 inline-flex items-center px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider bg-lime-300 text-slate-900 font-semibold">
                Enterprise
              </div>
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-lime-300 text-slate-900 shrink-0">
                <LayoutDashboard className="w-6 h-6" strokeWidth={1.8}/>
              </div>
              <div className="flex-1">
                <div className="text-[10.5px] mono uppercase tracking-wider text-lime-300/80 mb-1">Admin control center</div>
                <div className="text-[18px] font-semibold leading-tight">Network-wide oversight for your ops team.</div>
                <div className="text-[13px] text-white/65 mt-1.5 leading-relaxed">
                  Cross-supplier visibility on orders, invoices, tracking events, SLA breaches, and analytics. Built for ops teams running multi-supplier dropship at scale.
                </div>
              </div>
              <a href="/contact" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-lime-300 text-slate-900 text-[13px] font-semibold hover:bg-lime-400 transition-colors shrink-0">
                Talk to sales <ArrowRight className="w-3.5 h-3.5"/>
              </a>
            </div>
          </Reveal>
          </div>
        </div>
      </section>

      {/* ─────────────────── THE MATH ─────────────────── */}
      <section id="math" className="relative py-20 md:py-24 px-6 md:px-12 max-w-[1280px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-12 items-center">
          <Reveal>
            <div>
              <Eyebrow>The math</Eyebrow>
              <h2 className="mt-4 display text-3xl md:text-5xl font-semibold tracking-tight text-slate-900">
                Why dropship through verqo<br/>
                <span className="text-slate-400">vs. anywhere else.</span>
              </h2>
              <p className="mt-6 text-[16px] text-slate-600 leading-relaxed max-w-md">
                We didn't just rebuild dropship — we baked in the cashflow, risk, and ops layers that every other dropship platform forces you to bolt on separately.
              </p>
              <div className="mt-7 flex items-center gap-3 text-[13px] text-slate-700">
                <Zap className="w-4 h-4 text-lime-600"/>
                Setup pays for itself within the first 30 days for most brands.
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <TheMath/>
          </Reveal>
        </div>
      </section>

      {/* ─────────────────── LIVE FEED ─────────────────── */}
      <section className="relative py-20 md:py-24 px-6 md:px-12 max-w-[1280px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-12 items-center">
          <Reveal delay={0.05}>
            <LiveSyncFeed/>
          </Reveal>
          <Reveal>
            <div>
              <Eyebrow>The network · live</Eyebrow>
              <h2 className="mt-4 display text-3xl md:text-5xl font-semibold tracking-tight text-slate-900">
                Every event, <span className="text-slate-400">in real time.</span>
              </h2>
              <p className="mt-6 text-[16px] text-slate-600 leading-relaxed max-w-md">
                Inventory updates, orders, payments, shipping labels, credit decisions, claims — all flow through one ledger. No reconciliation. No spreadsheets. No surprise gaps.
              </p>
              <ul className="mt-7 space-y-2.5 text-[13.5px] text-slate-700">
                <li className="flex gap-2 items-start"><CheckCircle2 className="w-4 h-4 text-lime-600 mt-0.5 shrink-0"/>Webhooks fire to both sides on every state change</li>
                <li className="flex gap-2 items-start"><CheckCircle2 className="w-4 h-4 text-lime-600 mt-0.5 shrink-0"/>API access available on every plan</li>
                <li className="flex gap-2 items-start"><CheckCircle2 className="w-4 h-4 text-lime-600 mt-0.5 shrink-0"/>Audit trail hash-chained for SOC 2 / compliance</li>
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─────────────────── BUILT-IN MOATS ─────────────────── */}
      <section className="relative py-20 md:py-24 px-6 md:px-12 max-w-[1280px] mx-auto">
        <Reveal>
          <div className="text-center mb-12">
            <Eyebrow>The 4 things no one else bundles</Eyebrow>
            <h2 className="mt-4 display text-3xl md:text-5xl font-semibold tracking-tight text-slate-900">
              Only on verqo.
            </h2>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Banknote,    title: "Day-1 supplier payouts",   body: "We carry the float. Suppliers get paid within 24h, no matter what term the retailer takes." },
            { icon: ShieldCheck, title: "Every invoice insured",    body: "Allianz cover baked into every order. Zero bad-debt risk for suppliers." },
            { icon: Zap,         title: "AI credit in 60 seconds",  body: "Retailers approved for Net-30/60/90 instantly — no paper trade references, no fax-back forms." },
            { icon: Network,     title: "One portal, end-to-end",   body: "Dropship lives inside the same wholesale OS as POs, invoices, payments, and shipping." },
          ].map((m, i) => (
            <Reveal key={m.title} delay={i * 0.08}>
              <div className="rounded-2xl border border-slate-200 bg-white p-6 h-full hover:border-slate-900 hover:shadow-[0_24px_60px_-24px_rgba(15,23,42,0.25)] transition-all">
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-slate-900 text-lime-300 mb-4">
                  <m.icon className="w-5 h-5" strokeWidth={1.8}/>
                </div>
                <div className="text-[15px] font-semibold text-slate-900">{m.title}</div>
                <div className="text-[12.5px] text-slate-600 mt-2 leading-relaxed">{m.body}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ─────────────────── COMPARISON ─────────────────── */}
      <section id="vs" className="relative py-20 md:py-24 px-6 md:px-12 max-w-[1280px] mx-auto">
        <Reveal>
          <div className="text-center mb-12">
            <Eyebrow>The honest comparison</Eyebrow>
            <h2 className="mt-4 display text-3xl md:text-5xl font-semibold tracking-tight text-slate-900">
              verqo vs. everyone else.
            </h2>
            <p className="mt-4 text-[14.5px] text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Modern Dropship and Canal are great catalog tools. We use them as inspiration. But "catalog sync" is table stakes — the real value lives in the layers they don't touch.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <ComparisonTable/>
        </Reveal>
      </section>

      {/* ─────────────────── PRICING ─────────────────── */}
      <section id="pricing" className="relative py-20 md:py-24 px-6 md:px-12 max-w-[1280px] mx-auto">
        <Reveal>
          <div className="text-center mb-12">
            <Eyebrow>Pricing</Eyebrow>
            <h2 className="mt-4 display text-3xl md:text-5xl font-semibold tracking-tight text-slate-900">
              Free for retailers. <span className="text-slate-400">Aligned with suppliers.</span>
            </h2>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl mx-auto">
          <Reveal delay={0.05}>
            <div className="rounded-2xl border-2 border-lime-400 bg-lime-50/40 p-8 h-full">
              <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-lime-700 font-semibold">Retailers</div>
              <div className="mt-3 text-4xl font-bold tracking-tight">$0<span className="text-base font-normal text-slate-500">/month, forever</span></div>
              <div className="text-[13px] text-slate-700 mt-2">No subscription. No platform fees. No surprise charges.</div>
              <ul className="mt-6 space-y-2 text-[13px] text-slate-700">
                <li className="flex gap-2"><Check className="w-4 h-4 text-lime-700 mt-0.5 shrink-0"/>Unlimited dropship imports</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-lime-700 mt-0.5 shrink-0"/>Net-30/60/90 financing</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-lime-700 mt-0.5 shrink-0"/>API + webhooks included</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-lime-700 mt-0.5 shrink-0"/>Buyer cover via Allianz</li>
              </ul>
              <a href="/waitlist?as=buyer" className="mt-6 inline-flex items-center gap-2 text-[13.5px] font-semibold text-slate-900 group">
                Start dropshipping →
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="rounded-2xl border border-slate-200 bg-white p-8 h-full">
              <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-slate-500 font-semibold">Suppliers</div>
              <div className="mt-3 text-4xl font-bold tracking-tight">1–3%<span className="text-base font-normal text-slate-500"> per order</span></div>
              <div className="text-[13px] text-slate-700 mt-2">Rate depends on volume and the term length you accept.</div>
              <ul className="mt-6 space-y-2 text-[13px] text-slate-700">
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0"/>Day-1 payouts to your wallet</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0"/>Allianz invoice cover</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0"/>API + webhooks included</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0"/>Per-buyer pricing controls</li>
              </ul>
              <a href="/waitlist?as=seller" className="mt-6 inline-flex items-center gap-2 text-[13.5px] font-semibold text-slate-900 group">
                Onboard your catalog →
              </a>
            </div>
          </Reveal>
        </div>
        <p className="mt-7 text-center text-[11.5px] text-slate-500">
          Pass-through costs (carrier shipping, card processing, wire fees) apply normally and we don't mark them up.
        </p>
      </section>

      {/* ─────────────────── FAQ ─────────────────── */}
      <section className="relative py-20 md:py-24 px-6 md:px-12 max-w-[920px] mx-auto">
        <Reveal>
          <div className="text-center mb-12">
            <Eyebrow>Common questions</Eyebrow>
            <h2 className="mt-4 display text-3xl md:text-5xl font-semibold tracking-tight text-slate-900">
              Everything else.
            </h2>
          </div>
        </Reveal>
        <div className="space-y-2">
          {DROPSHIP_FAQS.map((f, i) => (
            <Reveal key={f.q} delay={i * 0.04}>
              <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-3 p-4 md:p-5 text-left hover:bg-slate-50 transition-colors"
                  data-testid={`faq-${i}`}
                >
                  <span className="text-[14.5px] md:text-[15px] font-semibold text-slate-900">{f.q}</span>
                  <ChevronDown className={`w-4 h-4 text-slate-500 shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`}/>
                </button>
                {openFaq === i && (
                  <div className="px-4 md:px-5 pb-5 text-[13.5px] text-slate-600 leading-relaxed">{f.a}</div>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ─────────────────── FINAL CTA ─────────────────── */}
      <section className="relative py-20 md:py-24 px-6 md:px-12 max-w-[1280px] mx-auto">
        <Reveal>
          <div className="rounded-3xl bg-[#0c1612] text-white p-10 md:p-16 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
                 style={{ backgroundImage: "linear-gradient(rgba(220,255,58,1) 1px, transparent 1px), linear-gradient(90deg, rgba(220,255,58,1) 1px, transparent 1px)", backgroundSize: "28px 28px" }}/>
            <div className="absolute inset-0 opacity-30 pointer-events-none"
                 style={{ background: "radial-gradient(circle at 50% 30%, rgba(220,255,58,0.18) 0%, transparent 60%)" }}/>
            <div className="relative">
              <Eyebrow>Ready to ship</Eyebrow>
              <h2 className="mt-4 display text-3xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white">
                Dropship the way it<br/>
                <span className="bg-lime-300 text-slate-900 px-3 -rotate-1 inline-block mt-2">should have worked.</span>
              </h2>
              <p className="mt-6 text-[15px] md:text-[16px] text-white/70 max-w-xl mx-auto leading-relaxed">
                Day-1 payouts. Allianz coverage. Net-90 financing. One portal. Get on the verqo network in under 24 hours.
              </p>
              <div className="mt-9 flex flex-wrap justify-center gap-3">
                <a href="/waitlist?as=seller" className="inline-flex items-center gap-2 px-7 py-4 rounded-lg bg-lime-300 text-slate-900 text-[14px] font-semibold hover:bg-lime-400 transition-colors">
                  Onboard as a supplier <ArrowRight className="w-4 h-4"/>
                </a>
                <a href="/waitlist?as=buyer" className="inline-flex items-center gap-2 px-7 py-4 rounded-lg border border-white/20 text-white text-[14px] hover:bg-white/5 transition-colors">
                  Get pre-approved as a retailer
                </a>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ─────────────────── FOOTER ─────────────────── */}
      <footer className="relative border-t border-slate-200 py-10 px-6 md:px-12 max-w-[1280px] mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 text-[11.5px] text-slate-500">
          <div className="flex items-center gap-2">
            <VerqoLockup small/>
            <span>· Dropship, the wholesale-OS way.</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/" className="hover:text-slate-900 transition-colors">For sellers</Link>
            <Link to="/buyers" className="hover:text-slate-900 transition-colors">For buyers</Link>
            <a href="/faq" className="hover:text-slate-900 transition-colors">FAQ</a>
            <a href="/contact" className="hover:text-slate-900 transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
